import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import * as CraService from '../../services/cra.service';
import { SOURCE_PROVIDER } from 'src/constants/companyReport';
import * as notify from 'src/services/notification.service';
import {
  ICraInternalObject,
  IRiskRatingForCraResponse,
  IInternalRiskRatingObject,
  ICraDetailsForUI,
  ICRADetails,
} from 'src/types/ICra';
import { PlaceHolderForRiskRating } from 'src/constants/cra.constants';
import RiskRating from './RiskRating';

// Input props of this component
interface Props {
  // to open/close the collapsable in the parent component
  handleOnClick: any;
  // gets list of alerts for screening (if applicable)
  screeningAlerts?: any;
  // default values of cra (refers to the json file on the S3)
  craDefaultValues: any;
  // API response for company details page passed down as prop
  companyReport?: any;
  // indicates when the last call of company reports has been done (i.e. calling alerts API at the moment)
  companyReportHasLoaded?: boolean;
  // To pass the current risk rating to the parent component
  updateCRADetails: Dispatch<SetStateAction<ICRADetails>>;
}

/**
 * Step component/function to:
 * 1. get the cra pre-req object
 * 2. pass that object to cra computation method
 * 3. return the cra risk/rating
 * @param Props
 * @returns risk rating/score (once finished)
 */
const CustomerRiskAssessment = ({
  handleOnClick,
  screeningAlerts,
  craDefaultValues,
  companyReport,
  companyReportHasLoaded,
  updateCRADetails,
}: Props) => {
  const [riskRating, setRiskRating] = useState(PlaceHolderForRiskRating);
  const [craDetails, setCraDetails] = useState<ICraDetailsForUI | undefined>(undefined);
  /**
   *This gets the
   * @param companyReport API response for company details page passed down as prop
   * @returns CraInternalObject which can be then passed to the CRA computation method
   */
  const getCraPreReqObject = (companyReport: any) => {
    try {
      const craobject = CraService.mapCompanyProfileToCraObject(
        // use the source's initial to get the provider name, else use the default one i.e. DnB
        SOURCE_PROVIDER[companyReport?.source] || SOURCE_PROVIDER['D'],
        companyReport?.companyProfile?.companyProfile,
      );
      console.log(craobject);
      return craobject;
    } catch (error: any) {
      console.log(error && error?.message);
      notify.error(error && error?.message);
    }
  };

  /**
   * Takes in the Pre req object and default values and returns risk rating
   * @param source source initials of data providers e.g. D, C, K etc
   * @param craPreReq ICraInternalObject
   * @param craDefaultValues default values json returned
   */
  const getCraComputationResult = (
    source: string,
    craRiskRatingForDnB: IRiskRatingForCraResponse,
  ) => {
    if (
      !source ||
      !craRiskRatingForDnB ||
      Object.hasOwn(craRiskRatingForDnB, 'craComputationsObj')
    ) {
    }

    try {
      const provider = SOURCE_PROVIDER[source];
      switch (provider) {
        case SOURCE_PROVIDER.D:
          // once the ratings are received, run the computation on that
          const craComputation: IInternalRiskRatingObject = CraService.getCraComputation(
            craRiskRatingForDnB?.craComputationsObj,
          )!;
          console.log(craComputation);
          return craComputation;
        default:
          console.log('Invalid provider name ' + provider);
          break;
      }
    } catch (error: any) {
      console.log(error && error?.message);
      notify.error(error && error?.message);
    }
  };

  /**
   * Takes in the Pre req object and default values and returns risk rating
   * @param source source initials of data providers e.g. D, C, K etc
   * @param craPreReq ICraInternalObject
   * @param craDefaultValues default values json returned
   */
  const getRiskRatingForCRA = (
    source: string,
    craPreReq: ICraInternalObject,
    craDefaultValues: any,
  ) => {
    try {
      const provider = SOURCE_PROVIDER[source];
      switch (provider) {
        case SOURCE_PROVIDER.D:
          const craRiskRatingForDnB = CraService.calculateRiskRatingForDnBCra(
            craPreReq,
            craDefaultValues,
          );
          return craRiskRatingForDnB;
        default:
          console.log('Invalid provider name ' + provider);
          break;
      }
    } catch (error: any) {
      console.log(error && error?.message);
      notify.error(error && error?.message);
    }
  };

  /**
   * Step function to call two main methods
   * 1. compile the cra pre requisite object by calling getCraPreReqObject()
   * 2. Get risk rating for that cra object
   * 3. Get the final computation result {rating, score}
   * @param companyReport
   * @returns risk/rating for CRA
   */
  const computeCRA = (companyReport: any, screeningAlerts: any) => {
    try {
      // 1. get the cra pre req object which is required for computations
      const craPreReq = getCraPreReqObject(companyReport);
      if (craPreReq === null) {
        console.log('CRA is not computed for the current data source. Please switch to DnB');
        // set error message here for UI
        // ToDo
        return;
      }
      if (!craPreReq || !Object.keys(craPreReq).length) {
        throw Error(
          'Missing or Incomplete CRA Pre req object. Requires activityCodes, customerType and countries',
        );
      }
      // if there are alerts, update corresponding override flag
      if (screeningAlerts && screeningAlerts.length) {
        craPreReq.hasAlerts = true;
      }
      console.log(craPreReq);
      // 2. use the above cra pre req object to get risk ratings
      const riskRating = getRiskRatingForCRA(companyReport?.source, craPreReq, craDefaultValues);

      if (!riskRating || !Object.keys(riskRating).length) {
        throw new Error(`Unable to map risk ratings from company profile and defaults`);
      }
      // 3. get cra result from risk rating obtained
      // kept for debugging purposes
      // console.log(JSON.stringify(riskRating.craDetailsForUI));
      const computationResult = getCraComputationResult(companyReport?.source, riskRating);
      setRiskRating(computationResult || PlaceHolderForRiskRating);

      // format cra object for for UI
      const formattedCraDetailsForUI = CraService.formatCraDetailsForUI(
        riskRating?.craDetailsForUI || {},
      );

      const CRADetails: ICRADetails = {
        ...formattedCraDetailsForUI,
        overallRiskRating: computationResult?.rating || PlaceHolderForRiskRating.rating,
      }

      setCraDetails(formattedCraDetailsForUI);
      updateCRADetails(CRADetails);
      console.log(JSON.stringify(formattedCraDetailsForUI));
      CraService.clearFields();
      return computationResult;
    } catch (error: any) {
      console.log(error && error?.message);
      notify.error(error && error?.message);
    }
  };

  /**
   * This gets triggered whenever there is a change in the dependencies
   */
  useEffect(() => {
    // make sure we have all data required and ready before formulating the cra pre-req object
    const preReqsAreComplete = !!(
      companyReportHasLoaded &&
      Object.keys(craDefaultValues).length &&
      screeningAlerts &&
      companyReport?.companyProfile
    );
    // if the pre-reqs are complete
    if (preReqsAreComplete) {
      computeCRA(companyReport, screeningAlerts);
    }
  }, [craDefaultValues, screeningAlerts, companyReport, companyReportHasLoaded]);

  return (
    <>
      <div className="accordion-heading" onClick={() => handleOnClick('cra')}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-4 align-items-center">
            <h4 className="mb-0">CUSTOMER RISK ASSESSMENT</h4>
            <img
              className="loading-icon"
              src={require('src/assets/images/icons/spinner.svg').default}
              alt="Loading Icon"
            />
          </div>
          <div>
            <img
              className="chevron-icon"
              src={require('src/assets/images/icons/chevron-down.svg').default}
              alt="chevron-down icon"
            />
          </div>
        </div>
      </div>
      <div className="accordion-content">
        {riskRating && craDetails && (
          <>
            {
              <div>
                <table className="accordion-table risk-table sm-th-md-td">
                  <thead>
                    <tr>
                      <th className="table-header no-text-wrap">Overall Risk Rating</th>
                      <th>{<RiskRating match={riskRating?.rating} />}</th>
                      <th></th>
                      <th></th>
                    </tr>
                    <tr>
                      <th>Risk Factor</th>
                      <th>Rating</th>
                      <th>Source</th>
                      <th>Value</th>
                    </tr>
                  </thead>

                  <tbody>
                    {/* Customer Typology */}
                    <tr>
                      <td className="no-text-wrap">{craDetails.customerType.riskFactor}</td>
                      <td>
                        <RiskRating match={craDetails.customerType.rating} />
                      </td>
                      <td className="no-text-wrap">{craDetails.customerType.source}</td>
                      <td>{craDetails.customerType.attribute}</td>
                    </tr>

                    {/* Jurisdiction */}
                    <tr>
                      <td className="no-text-wrap">
                        {Object.values(craDetails.jurisdiction)[0].riskFactor}
                      </td>
                      <td>
                        <RiskRating match={Object.values(craDetails.jurisdiction)[0].rating} />
                      </td>
                      <td></td>
                      <td></td>
                    </tr>

                    {Object.values(craDetails.jurisdiction).map((subValue) => {
                      return (
                        <tr key={subValue.source}>
                          <td></td>
                          <td>
                            <RiskRating match={subValue.rating} />
                          </td>
                          <td className="no-text-wrap">{subValue.source}</td>
                          <td>{subValue.attribute}</td>
                        </tr>
                      );
                    })}

                    {/* Nature of Business */}
                    <tr>
                      <td className="no-text-wrap">
                        {Object.values(craDetails.occupation)[0][0].riskFactor}
                      </td>
                      <td>
                        <RiskRating match={Object.values(craDetails.occupation)[0][0].rating} />
                      </td>
                      <td></td>
                      <td></td>
                    </tr>

                    {Object.values(craDetails.occupation).map((subValue) => {
                      return subValue.map((value) => {
                        return (
                          <tr key={value.source + value.attribute}>
                            <td></td>
                            <td>
                              <RiskRating match={value.rating} />
                            </td>
                            <td className="no-text-wrap">{value.source}</td>
                            <td>{value.attribute}</td>
                          </tr>
                        );
                      });
                    })}

                    {/* Red Flags */}
                    <tr>
                      <td>Red Flags</td>
                      <td>
                        <RiskRating
                          match={
                            craDetails.overrides.length ? craDetails.overrides[0].rating : 'None'
                          }
                        />
                      </td>
                      <td>
                        {craDetails.overrides.length && craDetails.overrides[0].hasAlerts
                          ? null
                          : '-'}
                      </td>
                      <td>
                        {craDetails.overrides.length && craDetails.overrides[0].hasAlerts
                          ? null
                          : '-'}
                      </td>
                    </tr>

                    {!!craDetails.overrides.length &&
                      craDetails.overrides[0].hasAlerts &&
                      craDetails.overrides.map((subValue) => {
                        return (
                          <tr key={subValue.source + subValue.attribute}>
                            <td></td>
                            <td>
                              <RiskRating match={subValue.rating} />
                            </td>
                            <td className="no-text-wrap">{subValue.source}</td>
                            <td>{subValue.attribute}</td>
                          </tr>
                        );
                      })}

                    {/* Delivery Channel */}
                    <tr className="disabled">
                      <td>Delivery Channel</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>

                    {/* Product */}
                    <tr className="disabled">
                      <td>Product</td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            }
          </>
        )}
      </div>
    </>
  );
};

export default CustomerRiskAssessment;
